/**
 * Live tokenizer + linter + completion context for DQL — the same shape as
 * sql-lint.ts so one editor serves both. The rules are the live-verified
 * grammar in docs/inventory/search-capture.md ("DQL grammar for the
 * builder") and what buildDql/parseDql in @bloo-team/api-client emit and accept:
 *
 *   stream=A[,B] [where F op V [AND|OR F op V]…] | duration 1h | groupby F
 *     | select F, count(F) | timeslice 5m | limit 100
 *
 * Field names are BARE (no `$`); string values are single-quoted; numbers
 * bare; `where` lives in the stream clause, never as a pipe stage.
 */
import { AGG_FUNCTIONS, DURATION_PRESETS } from '@bloo-team/api-client'
import type { Completion, CompletionContext, Diagnostic, Severity, Token, TokenType, Vocabulary } from './sql-lint'

export const DQL_STAGES = ['duration', 'limit', 'first', 'last', 'groupby', 'select', 'timeslice'] as const
const STAGES = new Set<string>(DQL_STAGES)
const HEAD_KEYWORDS = new Set(['stream', 'where', 'and', 'or', 'like', 'not', 'from', 'to'])
const AGGS = new Set<string>(AGG_FUNCTIONS)
/** Live-verified operators; `>=`, `<=`, `<>` were never probed. */
const VERIFIED_OPS = new Set(['=', '!=', '>', '<'])

const TOKEN_RE =
  /(\s+)|('(?:[^']|'')*'?)|(\$[A-Za-z_]\w*)|(\d+[smhd]\b|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})|(\d+(?:\.\d+)?)|([A-Za-z_][\w-]*|\*)|(!=|>=|<=|<>|[=<>])|([|(),])|([≥≤≠])|(.)/gy

export function tokenizeDql(src: string): Token[] {
  const out: Token[] = []
  TOKEN_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = TOKEN_RE.exec(src)) !== null) {
    const start = m.index
    const text = m[0]
    if (text === '') break
    let type: TokenType
    if (m[1]) type = 'ws'
    else if (m[2]) type = 'string'
    else if (m[3]) type = 'bad' // $-prefixed field: SQL habit
    else if (m[4]) type = 'number' // duration / timeslice literal, or an ISO timestamp
    else if (m[5]) type = 'number'
    else if (m[6]) {
      const lo = text.toLowerCase()
      type = HEAD_KEYWORDS.has(lo) || STAGES.has(lo) ? 'keyword' : AGGS.has(lo) ? 'function' : 'ident'
    } else if (m[7]) type = 'op'
    else if (m[8]) type = 'punct'
    else if (m[9]) type = 'bad'
    else type = 'bad'
    out.push({ type, text, start, end: start + text.length })
    if (TOKEN_RE.lastIndex === start) TOKEN_RE.lastIndex++
  }
  return out
}

const sig = (tokens: Token[]) => tokens.filter((t) => t.type !== 'ws')
const lo = (t: Token | undefined) => t?.text.toLowerCase() ?? ''
const isKw = (t: Token | undefined, k: string) => !!t && t.type === 'keyword' && lo(t) === k

/** Split significant tokens into stages at each `|`. */
function stages(s: Token[]): Token[][] {
  const out: Token[][] = [[]]
  for (const t of s) {
    if (t.type === 'punct' && t.text === '|') out.push([])
    else out[out.length - 1]!.push(t)
  }
  return out
}

/** Streams named in the head (`stream=A,B`), as written. */
export function dqlStreams(tokens: Token[]): string[] {
  const head = stages(sig(tokens))[0] ?? []
  if (!isKw(head[0], 'stream') || head[1]?.text !== '=') return []
  const names: string[] = []
  for (let i = 2; i < head.length; i++) {
    const t = head[i]!
    if (isKw(t, 'where')) break
    if (t.type === 'ident') names.push(t.text)
    else if (t.text !== ',') break
  }
  return names
}

function fieldPool(vocabulary: Vocabulary | null | undefined, streams: string[]): Set<string> | null {
  if (!vocabulary) return null
  const named = streams.filter((s) => s !== '*')
  const entries = Object.entries(vocabulary).filter(([k]) =>
    named.length ? named.some((s) => s.toLowerCase() === k.toLowerCase()) : true,
  )
  if (named.length && entries.length !== named.length) return null // an unknown stream: cannot judge fields
  return new Set(entries.flatMap(([, fs]) => fs.map((f) => f.replace(/^\$/, '').toLowerCase())))
}

export function lintDql(src: string, vocabulary?: Vocabulary | null): Diagnostic[] {
  const tokens = tokenizeDql(src)
  const s = sig(tokens)
  const out: Diagnostic[] = []
  const push = (severity: Severity, t: Token, message: string) =>
    out.push({ severity, message, start: t.start, end: t.end })
  if (!s.length) return out

  const parts = stages(s)
  const head = parts[0]!
  const streams = dqlStreams(tokens)
  const knownStreams = vocabulary ? Object.keys(vocabulary) : null
  const fields = fieldPool(vocabulary, streams)

  // Anything not `stream=` is a form this lint never verified — stay quiet.
  if (!isKw(head[0], 'stream')) return out
  if (head[1]?.text !== '=' || !head[2]) {
    push('error', head[0]!, 'stream=<name> — name a stream, or * for all')
    return out
  }

  // --- head: stream list then optional where ------------------------------
  let i = 2
  for (; i < head.length; i++) {
    const t = head[i]!
    if (isKw(t, 'where')) break
    if (t.text === ',') continue
    if (t.type === 'bad' && t.text.startsWith('$')) {
      push('error', t, `stream names are bare — stream=${t.text.slice(1)}`)
      continue
    }
    if (t.type !== 'ident') {
      push('error', t, `unexpected ${t.text} in the stream list`)
      continue
    }
    if (t.text !== '*' && knownStreams && !knownStreams.some((k) => k.toLowerCase() === t.text.toLowerCase())) {
      push('error', t, `unknown stream ${t.text}`)
    }
  }

  if (i < head.length) {
    // where F op V (AND|OR …)
    const conds = head.slice(i + 1)
    if (!conds.length) push('error', head[i]!, 'where needs a condition — Field = \'value\'')
    let j = 0
    while (j < conds.length) {
      const f = conds[j]
      const op = conds[j + 1]
      const v = conds[j + 2]
      if (!f) break
      if (f.type === 'bad' && f.text.startsWith('$')) push('error', f, `DQL fields are bare — ${f.text.slice(1)}`)
      else if (f.type !== 'ident') push('error', f, `expected a field name, got ${f.text}`)
      else if (fields && !fields.has(f.text.toLowerCase())) {
        push('warn', f, `${f.text} is not a field of ${streams.join(',')}`)
      }
      // operator: = != > < LIKE, NOT LIKE
      let opEnd = j + 2
      if (op && isKw(op, 'not') && isKw(conds[j + 2], 'like')) opEnd = j + 3
      else if (op && (op.type === 'op' || isKw(op, 'like'))) {
        if (op.type === 'op' && !VERIFIED_OPS.has(op.text)) push('warn', op, `${op.text} is not a verified DQL operator — =, !=, >, <, LIKE, NOT LIKE are`)
      } else if (op?.type === 'bad') {
        push('error', op, `'${op.text}' is a unicode glyph — retype it as an ASCII operator`)
      } else {
        push('error', op ?? f, 'expected an operator: =, !=, >, <, LIKE, NOT LIKE')
        break
      }
      const val = opEnd === j + 3 ? conds[j + 3] : v
      if (!val) {
        push('error', conds[opEnd - 1] ?? f, 'expected a value')
        break
      }
      if (val.type === 'ident') push('error', val, `string values are single-quoted — '${val.text}'`)
      else if (val.type === 'string' && !/'$/.test(val.text.slice(1))) push('error', val, 'unterminated string')
      else if (val.type !== 'string' && val.type !== 'number') push('error', val, `unexpected ${val.text}`)
      j = opEnd + 1
      const join = conds[j]
      if (!join) break
      if (isKw(join, 'and') || isKw(join, 'or')) j++
      else {
        push('error', join, 'join conditions with AND or OR')
        break
      }
    }
  }

  // --- stages ---------------------------------------------------------------
  let sawGroupby: Token | null = null
  let sawSelect = false
  for (const st of parts.slice(1)) {
    const k = st[0]
    if (!k) {
      push('error', s[s.length - 1]!, 'empty stage after |')
      continue
    }
    const name = lo(k)
    if (isKw(k, 'where')) {
      push('error', k, "where belongs in the stream clause, not after a | — stream=X where … | duration 1h")
      continue
    }
    if (!STAGES.has(name)) {
      push('warn', k, `${k.text} is not a verified stage — ${DQL_STAGES.join(', ')} are`)
      continue
    }
    const arg = st[1]
    const TS = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/
    if (name === 'duration' && isKw(arg, 'from')) {
      // duration from <ts> to <ts> — absolute window inside the query.
      const [, , a, toKw, b, extra] = st
      if (!a || !TS.test(a.text)) push('error', a ?? arg!, 'duration from takes YYYY-MM-DDTHH:MM:SS')
      else if (!isKw(toKw, 'to')) push('error', toKw ?? a, 'duration from <start> to <end>')
      else if (!b || !TS.test(b.text)) push('error', b ?? toKw!, 'duration … to takes YYYY-MM-DDTHH:MM:SS')
      else if (a.text >= b.text) push('error', b, 'end must be after start')
      if (extra) push('error', extra, `unexpected ${extra.text} after the window`)
    } else if (name === 'duration' || name === 'timeslice') {
      if (!arg || !/^\d+[smhd]$/i.test(arg.text)) {
        push('error', arg ?? k, name === 'duration'
          ? 'duration takes <n><m|h|d> (duration 1h) or from <ts> to <ts>'
          : 'timeslice takes <n><m|h|d>, e.g. timeslice 5m')
      }
    } else if (name === 'limit' || name === 'first' || name === 'last') {
      if (!arg || !/^\d+$/.test(arg.text)) push('error', arg ?? k, `${k.text} takes a whole number`)
    } else if (name === 'groupby') {
      sawGroupby = k
      for (const t of st.slice(1)) {
        if (t.text === ',') continue
        if (t.type === 'bad' && t.text.startsWith('$')) push('error', t, `DQL fields are bare — ${t.text.slice(1)}`)
        else if (t.type !== 'ident') push('error', t, `expected a field name, got ${t.text}`)
        else if (fields && !fields.has(t.text.toLowerCase())) push('warn', t, `${t.text} is not a field of ${streams.join(',')}`)
      }
      if (st.length < 2) push('error', k, 'groupby needs a field')
    } else if (name === 'select') {
      sawSelect = true
      if (st.length < 2) push('error', k, 'select needs at least one field')
      for (let x = 1; x < st.length; x++) {
        const t = st[x]!
        if (t.text === ',' || t.text === '(' || t.text === ')') continue
        if (t.type === 'function') {
          if (st[x + 1]?.text !== '(') push('error', t, `${t.text} needs an argument — ${t.text}(Field)`)
          continue
        }
        if (t.type === 'bad' && t.text.startsWith('$')) push('error', t, `DQL fields are bare — ${t.text.slice(1)}`)
        else if (t.type === 'ident' && t.text !== '*' && fields && !fields.has(t.text.toLowerCase())) {
          push('warn', t, `${t.text} is not a field of ${streams.join(',')}`)
        } else if (t.type !== 'ident') push('error', t, `unexpected ${t.text} in select`)
      }
    }
  }
  if (sawGroupby && !sawSelect) push('hint', sawGroupby, 'groupby usually pairs with | select Field, count(Field)')

  return out.sort((a, b) => a.start - b.start)
}

/* --------------------------------------------------------------- completion */

export function completeDql(src: string, caret: number, vocabulary?: Vocabulary | null): CompletionContext | null {
  const tokens = tokenizeDql(src)
  // The word under the caret — only word-like tokens count; a caret right
  // after `=` or `(` is at the START of something new, not inside the symbol.
  const WORDS = new Set(['ident', 'keyword', 'function', 'field', 'bad', 'string', 'number'])
  const at = tokens.find((t) => t.start < caret && caret <= t.end && WORDS.has(t.type))
  if (at?.type === 'string' || at?.type === 'number') return null
  const before = sig(tokens.filter((t) => t.end <= (at ? at.start : caret)))
  const prev = before[before.length - 1]
  const prefix = at ? src.slice(at.start, caret) : ''
  const from = at ? at.start : caret
  const to = at ? at.end : caret
  const has = (label: string) => label.toLowerCase().startsWith(prefix.toLowerCase())
  const done = (items: Completion[]) => (items.length ? { from, to, prefix, items: items.slice(0, 40) } : null)
  // Which stage the caret is in: the tokens since the last `|`.
  const stage: Token[] = []
  for (let k = before.length - 1; k >= 0; k--) {
    const t = before[k]!
    if (t.type === 'punct' && t.text === '|') break
    stage.unshift(t)
  }
  const inHead = stage.length === before.length
  const streams = dqlStreams(tokens)
  const fieldItems = (): Completion[] => {
    if (!vocabulary) return []
    const named = streams.filter((s) => s !== '*')
    const entries = Object.entries(vocabulary).filter(([k]) =>
      named.length ? named.some((s) => s.toLowerCase() === k.toLowerCase()) : true,
    )
    const pool = [...new Set(entries.flatMap(([, fs]) => fs.map((f) => f.replace(/^\$/, ''))))]
    return pool.filter(has).sort((a, b) => a.localeCompare(b)).map((f) => ({ label: f, kind: 'field' as const, insert: f }))
  }

  // Empty box: offer the head.
  if (!before.length && !at) return done([{ label: 'stream=', kind: 'keyword', insert: 'stream=' }])
  if (at && !prev && has('stream')) return done([{ label: 'stream=', kind: 'keyword', insert: 'stream=' }])

  // After `|`: stages.
  if (prev?.text === '|' || (stage.length === 1 && at && !inHead)) {
    return done(DQL_STAGES.filter(has).map((k) => ({ label: k, kind: 'keyword' as const, insert: `${k} ` })))
  }

  if (inHead) {
    // Stream list: after `=` or `,` before any where.
    const whereAt = stage.findIndex((t) => isKw(t, 'where'))
    if (whereAt === -1 && (prev?.text === '=' || prev?.text === ',')) {
      if (!vocabulary) return null
      return done(
        ['*', ...Object.keys(vocabulary)].filter(has).map((k) => ({ label: k, kind: 'stream' as const, insert: k })),
      )
    }
    // After a complete stream list: offer `where`.
    if (whereAt === -1 && prev?.type === 'ident' && (!at || has('where'))) {
      return done([{ label: 'where', kind: 'keyword', insert: 'where ' }])
    }
    // Conditions: field after where/AND/OR; operator after a field; joins after a value.
    if (isKw(prev, 'where') || isKw(prev, 'and') || isKw(prev, 'or')) return done(fieldItems())
    if (prev?.type === 'ident' && whereAt !== -1) {
      return done(
        ['=', '!=', '>', '<', 'LIKE', 'NOT LIKE'].filter(has).map((o) => ({ label: o, kind: 'keyword' as const, insert: `${o} ` })),
      )
    }
    if ((prev?.type === 'string' || prev?.type === 'number') && whereAt !== -1) {
      return done(['AND', 'OR'].filter(has).map((o) => ({ label: o, kind: 'keyword' as const, insert: `${o} ` })))
    }
    return null
  }

  const head = lo(stage[0])
  if (head === 'duration' || head === 'timeslice') {
    if (stage.length >= 2 && head === 'duration') {
      // duration from <ts> [to <ts>]
      if (stage.length === 3 && stage[2]!.type === 'number') return done([{ label: 'to', kind: 'keyword', insert: 'to ' }])
      return null
    }
    const presets = head === 'duration' ? DURATION_PRESETS.map((p) => p.value) : ['1m', '5m', '15m', '1h', '1d']
    const items: Completion[] = presets.filter(has).map((p) => ({ label: p, kind: 'keyword' as const, insert: p }))
    if (head === 'duration' && has('from')) items.push({ label: 'from <start> to <end>', kind: 'keyword', insert: 'from ' })
    return done(items)
  }
  if (head === 'groupby') return done(fieldItems())
  if (head === 'select') {
    if (prev?.text === '(') return done(fieldItems())
    const fns = AGG_FUNCTIONS.filter(has).map((f) => ({ label: `${f}()`, kind: 'function' as const, insert: `${f}(` }))
    return done([...fieldItems(), ...fns])
  }
  return null
}
